'use client';

import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Check, X, Tv, Star, LayoutGrid, Palette, Layers, Search,
  Activity, BarChart3, ArrowLeft, Shield, Smartphone, CheckCircle2, Infinity, Crown, Zap
} from 'lucide-react';
import Link from 'next/link';
import { auth, db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';

interface PricingFeature {
  name: string;
  included: boolean;
  icon?: React.ReactNode;
}

interface PricingPlan {
  id: string;
  name: string;
  price: string;
  originalPrice?: string;
  period?: string;
  badge?: string;
  badgeColor?: string;
  description: string;
  features: PricingFeature[];
  highlight: boolean;
  accentClass: string;
  iconEl: React.ReactNode;
}

const COMMON_FEATURES: PricingFeature[] = [
  { name: 'Multiple Playlists', included: true },
  { name: 'M3U/M3U8 Support', included: true },
  { name: 'High Performance Player', included: true },
  { name: 'Recording (All Qualities)', included: true },
  { name: 'Favorites System', included: true, icon: <Star size={14} className="text-yellow-400" /> },
  { name: 'Grid View Toggle', included: true, icon: <LayoutGrid size={14} className="text-indigo-400" /> },
  { name: 'Theme Customization', included: true, icon: <Palette size={14} className="text-pink-400" /> },
  { name: 'Multi-Screen Mode', included: true, icon: <Layers size={14} className="text-blue-400" /> },
  { name: 'Advanced Search', included: true, icon: <Search size={14} className="text-green-400" /> },
  { name: 'Stream Health Checker', included: true, icon: <Activity size={14} className="text-red-400" /> },
  { name: 'Bandwidth Monitor', included: true, icon: <BarChart3 size={14} className="text-cyan-400" /> },
];

const plans: PricingPlan[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: '$4.99',
    period: '/month',
    description: 'Full premium access, billed monthly',
    features: COMMON_FEATURES,
    highlight: false,
    accentClass: 'border-white/10',
    iconEl: <Zap className="text-indigo-400" size={28} />,
  },
  {
    id: 'annual',
    name: 'Annual',
    price: '$39.99',
    originalPrice: '$59.88',
    period: '/year',
    badge: 'Save 33%',
    badgeColor: 'bg-green-500 text-black',
    description: 'Best value — ~$3.33/mo billed annually',
    features: COMMON_FEATURES,
    highlight: true,
    accentClass: 'border-indigo-500/40',
    iconEl: <Crown className="text-yellow-400" size={28} />,
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: '$99.99',
    period: 'one-time',
    badge: 'Best Deal',
    badgeColor: 'bg-purple-500 text-white',
    description: 'Pay once, enjoy forever',
    features: COMMON_FEATURES,
    highlight: false,
    accentClass: 'border-purple-500/30',
    iconEl: <Infinity className="text-purple-400" size={28} />,
  },
];

export default function PricingPage() {
  const [userPlan, setUserPlan] = useState<'free' | 'paid'>('free');

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u: User | null) => {
      if (u) {
        const userRef = doc(db, 'users', u.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserPlan(userSnap.data().plan || 'free');
        }
      } else {
        setUserPlan('free');
      }
    });
    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-[#030303] text-gray-100 font-sans selection:bg-indigo-500/30 overflow-x-hidden">
      {/* Dynamic Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/10 blur-[120px] rounded-full" />
        <div className="absolute top-[40%] left-[50%] w-[30%] h-[30%] bg-blue-600/5 blur-[100px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 md:py-20 lg:py-24">
        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-16 md:mb-24"
        >
          <Link href="/" className="flex items-center gap-3 text-gray-400 hover:text-white transition-all group px-4 py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-md">
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Tv className="text-indigo-500" size={24} />
            <span className="font-bold tracking-tighter text-xl">IPTV GO</span>
          </div>
        </motion.div>

        {/* Header Section */}
        <div className="flex flex-col items-center text-center mb-20 md:mb-28">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-6"
          >
            <Star size={12} className="fill-indigo-400" />
            Premium Access
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 leading-[0.9] md:leading-[1]"
          >
            Unlock the <br />
            <span className="bg-gradient-to-r from-indigo-400 via-white to-purple-400 bg-clip-text text-transparent">Full Experience</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg md:text-xl max-w-2xl leading-relaxed"
          >
            Join thousands of users enjoying high-quality streaming, advanced tools, and zero limitations. Subscribe directly from the <strong className="text-white">IPTV GO app</strong> on your device.
          </motion.p>

          {/* App Store CTA banner */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-10 flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl"
          >
            <Smartphone size={18} className="text-indigo-400 shrink-0" />
            <p className="text-sm text-gray-300">
              Subscriptions are managed via <span className="text-white font-semibold">App Store</span> or <span className="text-white font-semibold">Google Play</span> inside the app
            </p>
          </motion.div>

          {userPlan === 'paid' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 rounded-2xl border backdrop-blur-xl bg-green-500/10 border-green-500/30 text-green-400 flex items-center gap-3"
            >
              <CheckCircle2 size={18} />
              <span className="text-sm font-medium">You have an active Premium subscription.</span>
            </motion.div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto relative px-2">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * (idx + 1), duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
              className={`group relative p-8 rounded-[36px] border transition-all duration-500 ${
                plan.highlight
                  ? 'bg-gradient-to-b from-indigo-600/20 via-indigo-600/5 to-transparent border-indigo-500/40 shadow-2xl shadow-indigo-600/15 scale-[1.02]'
                  : `bg-white/[0.02] ${plan.accentClass} hover:border-white/20`
              } backdrop-blur-2xl overflow-hidden`}
            >
              {/* Top accent line */}
              {plan.highlight && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent blur-sm rounded-full" />
              )}
              {plan.id === 'lifetime' && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent blur-sm rounded-full" />
              )}

              <div className="relative z-10">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-2xl bg-white/5 border border-white/10">
                      {plan.iconEl}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{plan.description}</p>
                    </div>
                  </div>
                  {plan.badge && (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-lg ${plan.badgeColor}`}>
                      {plan.badge}
                    </span>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-black tracking-tighter text-white">{plan.price}</span>
                  <span className="text-gray-500 font-semibold text-sm">{plan.period}</span>
                </div>
                {plan.originalPrice && (
                  <p className="text-xs text-gray-600 line-through mb-6">{plan.originalPrice}/year</p>
                )}
                {!plan.originalPrice && <div className="mb-6" />}

                {/* Features */}
                <div className="space-y-3 mb-10">
                  {plan.features.map((f) => (
                    <div key={f.name} className="flex items-center gap-3">
                      <div className={`p-1 rounded-full ${f.included ? 'bg-indigo-500/20 text-indigo-400' : 'bg-gray-800 text-gray-500'}`}>
                        {f.included ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-300">{f.name}</span>
                        {f.icon && <span className="opacity-60">{f.icon}</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                {userPlan === 'paid' ? (
                  <div className="flex items-center justify-center gap-2 w-full py-4 bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 rounded-2xl font-bold text-sm">
                    <CheckCircle2 size={16} />
                    Active Plan
                  </div>
                ) : (
                  <div className={`w-full py-4 rounded-2xl text-center font-bold text-sm flex flex-col items-center gap-1 ${
                    plan.highlight
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                      : plan.id === 'lifetime'
                      ? 'bg-purple-600/80 text-white shadow-lg shadow-purple-600/20'
                      : 'bg-white/10 text-white border border-white/20'
                  }`}>
                    <div className="flex items-center gap-2">
                      <Smartphone size={14} />
                      Get in IPTV GO App
                    </div>
                    <span className="text-[10px] font-normal opacity-70">Available on iOS &amp; Android</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* How to Subscribe */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-24 max-w-3xl mx-auto text-center"
        >
          <p className="text-xs text-gray-600 font-bold uppercase tracking-widest mb-6">How to subscribe</p>
          <div className="grid grid-cols-3 gap-6">
            {[
              { step: '1', text: 'Download the IPTV GO app on your device' },
              { step: '2', text: 'Sign in with your account and go to Settings' },
              { step: '3', text: 'Choose your plan and complete purchase via App Store or Play Store' },
            ].map(({ step, text }) => (
              <div key={step} className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 font-black text-sm">
                  {step}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Footer / Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-24 pt-16 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
        >
          <div className="space-y-3">
            <Shield className="text-indigo-400 mb-2" size={24} />
            <h4 className="font-bold text-sm">Secure Payment</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Handled securely by Apple App Store &amp; Google Play.</p>
          </div>
          <div className="space-y-3">
            <Smartphone className="text-blue-400 mb-2" size={24} />
            <h4 className="font-bold text-sm">In-App Purchase</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Subscribe directly inside the app — no third-party website needed.</p>
          </div>
          <div className="space-y-3">
            <CheckCircle2 className="text-green-400 mb-2" size={24} />
            <h4 className="font-bold text-sm">Easy to Cancel</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Cancel anytime through your App Store or Play Store subscription settings.</p>
          </div>
          <div className="space-y-3">
            <CheckCircle2 className="text-indigo-400 mb-2" size={24} />
            <h4 className="font-bold text-sm">24/7 Support</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Our dedicated team is always ready to help you out.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
